import { useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Textarea } from '@travellegacy/nexops-design-system';
import { useCreatePreparationForm } from '../hooks/usePreparationForms';
import { MoodSelector } from '../components/MoodSelector';
import type { CreatePreparationFormInput } from '../types';

/**
 * Página do Formulário de Preparação para Check-in
 */
export function PreparationFormPage() {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const [formData, setFormData] = useState<CreatePreparationFormInput>({
    month: currentMonth,
    year: currentYear,
    contributions: '',
    challenge: '',
    pdiSelfAssessment: 3,
    needsFromManager: '',
    additionalComments: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const createForm = useCreatePreparationForm();

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async () => {
    // Validações
    const newErrors: Record<string, string> = {};

    if (!formData.contributions.trim()) {
      newErrors.contributions = 'Campo obrigatório';
    }

    if (!formData.challenge.trim()) {
      newErrors.challenge = 'Campo obrigatório';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await createForm.mutateAsync(formData);

    // Resetar formulário
    setFormData({
      month: currentMonth,
      year: currentYear,
      contributions: '',
      challenge: '',
      pdiSelfAssessment: 3,
      needsFromManager: '',
      additionalComments: '',
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Formulário de Preparação</h1>
        <p className="text-muted-foreground">
          Preencha antes do check-in mensal - Período: {currentMonth}/{currentYear}
        </p>
      </div>

      {/* Formulário */}
      <Card>
        <CardHeader>
          <CardTitle>Preparação para Check-in Mensal</CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Contribuições */}
          <Textarea
            label="1. Quais foram suas maiores contribuições neste mês?"
            value={formData.contributions}
            onChange={(e) => handleChange('contributions', e.target.value)}
            error={errors.contributions}
            placeholder="Descreva suas principais entregas, conquistas e impactos positivos..."
            rows={4}
          />

          {/* Desafio */}
          <Textarea
            label="2. Qual foi seu maior desafio do mês?"
            value={formData.challenge}
            onChange={(e) => handleChange('challenge', e.target.value)}
            error={errors.challenge}
            placeholder="Relate dificuldades enfrentadas, bloqueios ou situações desafiadoras..."
            rows={4}
          />

          {/* Autoavaliação PDI */}
          <div>
            <MoodSelector
              label="3. Como você avalia seu progresso no PDI este mês?"
              value={formData.pdiSelfAssessment}
              onChange={(value) => handleChange('pdiSelfAssessment', value)}
              error={errors.pdiSelfAssessment}
            />
            <p className="text-xs text-muted-foreground mt-2">
              Avalie de 1 (nenhum progresso) a 5 (excelente progresso) nos objetivos do seu PDI
            </p>
          </div>

          {/* Pedidos ao Gestor */}
          <Textarea
            label="4. Do que você precisa do seu gestor?"
            value={formData.needsFromManager}
            onChange={(e) => handleChange('needsFromManager', e.target.value)}
            placeholder="Descreva apoios, recursos, feedback ou orientações que você precisa..."
            rows={3}
          />

          {/* Comentários Adicionais */}
          <Textarea
            label="5. Comentários adicionais (opcional)"
            value={formData.additionalComments}
            onChange={(e) => handleChange('additionalComments', e.target.value)}
            placeholder="Qualquer outra informação que você queira compartilhar..."
            rows={3}
          />

          {/* Botão Submit */}
          <div className="flex justify-end gap-2 pt-4">
            <Button onClick={handleSubmit} disabled={createForm.isPending}>
              {createForm.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Enviar Formulário
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Informação */}
      <Card className="bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800">
        <CardContent className="pt-6">
          <p className="text-sm text-blue-900 dark:text-blue-100">
            <strong>💡 Dica:</strong> Este formulário é para você se preparar antes do check-in mensal
            com seu gestor. Suas respostas ajudam a tornar a conversa mais produtiva e focada.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
